import React from "react";
import { Box, Container, Typography } from "@mui/material";
import Reportdiagram from "../assets/Reportdiagram.png";
import Report from "../assets/Report.png";
import { Helmet } from "react-helmet";

export default function ReportsSection() {
  return (
    <>
      <Helmet>
        <title>Reports & Insights - Namami India Support Services</title>
        <meta
          name="description"
          content="Access Namami India's detailed performance reports, project updates, and operational insights that highlight our dedication to transparency and quality."
        />
        <meta
          name="keywords"
          content="Namami India, reports, insights, performance, facility management reports, service metrics"
        />
        <meta property="og:title" content="Reports & Insights - Namami India" />
        <meta
          property="og:description"
          content="Get a detailed look into Namami India's operational excellence, performance data, and transparent reporting system."
        />
        <meta
          property="og:image"
          content="https://www.namamiindia.co.in/assets/namami-logo.png"
        />
        <meta
          property="og:url"
          content="https://www.namamiindia.co.in/reports"
        />
        <link rel="canonical" href="https://www.namamiindia.co.in/reports" />
      </Helmet>

      {/* === Section 1: Intro & Image === */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: "#fff" }}>
        <Container
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: { xs: 6, md: 8 },
          }}
        >
          {/* === Left Image === */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={Reportdiagram}
              alt="Reports and Insights Diagram"
              sx={{
                width: { xs: "90%", sm: "80%", md: "100%" },
                borderRadius: 0,
                boxShadow: "none",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* === Right Text === */}
          <Box
            sx={{
              flex: 1,
              textAlign: { xs: "center", md: "left" },
              maxWidth: 620,
            }}
          >
            <Typography
              variant="h3"
              fontWeight={900}
              mb={2}
              sx={{
                background: "linear-gradient(90deg, #1565c0, #004ba0)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Reports & Value Addition
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ lineHeight: 1.8, fontSize: "1.08rem", mb: 3 }}
            >
              In every operation, data tells the real story. At{" "}
              <strong>Namami India</strong>, we transform that story into
              actionable insight. Our comprehensive reporting ecosystem bridges
              performance metrics with real-world outcomes — helping clients
              visualize operational health, cost efficiency, and service quality
              at every level.
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ lineHeight: 1.8, mb: 3 }}
            >
              Our <strong>reports and dashboards</strong> are designed to go
              beyond routine tracking. They empower clients to understand how
              each process performs, how it aligns with SLAs, and where we can
              enhance efficiency. From predictive maintenance alerts to energy
              optimization graphs — every insight drives smarter decisions.
            </Typography>

            <Box
              sx={{
                mt: 3,
                p: 2.5,
                background: "linear-gradient(135deg, #e3f2fd, #ffffff)",
                borderRadius: 2,
                borderLeft: "4px solid #1976d2",
              }}
            >
              <Typography variant="h6" fontWeight={700} color="primary" mb={1}>
                Transparency at Every Step
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ lineHeight: 1.7 }}
              >
                We believe that informed clients are empowered clients. That’s
                why we maintain complete transparency — through structured daily,
                monthly, and annual reports backed by verifiable data and
                intelligent visualization.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* === Section 2: Hero Inverse Section === */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          background: "linear-gradient(180deg, #004ba0, #002d6b)",
          color: "white",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" fontWeight={800} mb={3}>
            Insights that Empower Decisions
          </Typography>
          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.9,
              fontSize: "1.1rem",
              opacity: 0.95,
            }}
          >
            Our analytics-driven approach turns information into intelligence.
            Every report is a roadmap — guiding our teams and clients to achieve
            sustainable excellence. Whether it’s tracking manpower productivity,
            monitoring asset performance, or ensuring compliance, our
            data-backed reports enable accountability and informed action.
            <br />
            <br />
            We continuously evolve our systems with digital MIS, IoT-based
            sensors, and cloud reporting — ensuring that performance metrics are
            always real-time, reliable, and ready for decision-making.
          </Typography>
        </Container>
      </Box>

      {/* === Section 3: Closing Image & Summary === */}
      <Box sx={{ py: { xs: 10, md: 14 }, backgroundColor: "#f8f9fa" }}>
        <Container maxWidth="lg" sx={{ textAlign: "center" }}>
          <Typography
            variant="h4"
            fontWeight={800}
            mb={4}
            sx={{
              background: "linear-gradient(90deg, #1565c0, #004ba0)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Data. Detail. Dedication.
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              lineHeight: 1.9,
              fontSize: "1.06rem",
              maxWidth: 900,
              mx: "auto",
              mb: 6,
            }}
          >
            Every report at Namami India is a reflection of our values —
            <strong> transparency, accountability, and precision.</strong> Our
            experts ensure that no data point is just a number; it’s an insight,
            an opportunity to optimize, and a commitment to our clients’
            continued growth.
          </Typography>

          <Box
            component="img"
            src={Report}
            alt="Operational Reports Overview"
            sx={{
              width: { xs: "80%", sm: "70%", md: "55%" },
              borderRadius: 0,
              boxShadow: "none",
              objectFit: "contain",
            }}
          />
        </Container>
      </Box>
    </>
  );
}
